import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "../api/client";
import type { TestItem } from "../api/types";
import { fmtDur } from "../components/Modal";
import { playError, registerAudio, unlock } from "../components/player";
import { Toast } from "../components/PlaylistPicker";
import { Deck, type DeckData, syncTo } from "../components/deck/engine";
import { decodePeaks } from "../components/deck/grid";
import { DeckPanel } from "../components/deck/DeckPanel";

/**
 * The test list and the two decks that play it.
 *
 * Two <audio> elements that may play together -- the one place in the app
 * where that is the point -- behind a single stopper, so a card starting on
 * the Browse page silences both. A track is loaded onto a slot from the list
 * below; its waveform and grid come from the API, its audio from the same
 * stream URL the rest of the app plays, and the deck object owns both from
 * then on. Items the worker has not got to yet say so, and the page keeps
 * asking until they are ready.
 */

type SlotName = "A" | "B";
type Slot = { item: TestItem | null; status: string | null };

const POLL_MS = 3000;

export function Decks() {
  const [items, setItems] = useState<TestItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const clearToast = useCallback(() => setToast(null), []);
  const [slots, setSlots] = useState<Record<SlotName, Slot>>({
    A: { item: null, status: null }, B: { item: null, status: null },
  });
  // Bumped whenever a deck says a control's reading changed.
  const [, setTick] = useState(0);

  const decks = useRef<Record<SlotName, Deck> | null>(null);
  if (!decks.current) decks.current = { A: new Deck(), B: new Deck() };
  const A = decks.current.A, B = decks.current.B;

  useEffect(() => {
    const bump = () => setTick((t) => t + 1);
    A.onChange = bump;
    B.onChange = bump;
    const unregister = registerAudio(() => { A.pause(); B.pause(); });
    return () => {
      A.onChange = null; B.onChange = null;
      A.unload(); B.unload();
      unregister();
    };
  }, [A, B]);

  // --- the list ---------------------------------------------------------------
  const load = useCallback(() => {
    api.testList()
      .then((v) => { setItems(v.items); setError(null); })
      .catch((e: ApiError) => setError(e.message));
  }, []);
  useEffect(load, [load]);

  // While the worker still owes something, ask again every few seconds. The
  // GET itself is what keeps the job queued, so this is also the nudge.
  const waiting = items?.some((it) => it.deck === "queued") ?? false;
  useEffect(() => {
    if (!waiting) return;
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [waiting, load]);

  // --- loading a slot -----------------------------------------------------------
  async function loadSlot(name: SlotName, it: TestItem) {
    const deck = name === "A" ? A : B;
    if (!it.release || it.pos == null) return;
    const release = it.release, pos = it.pos;
    unlock(deck.el); // inside the tap, for iOS
    setSlots((s) => ({ ...s, [name]: { item: it, status: "loading…" } }));
    try {
      const [d, set] = await Promise.all([api.deck(release, pos), api.streams(release)]);
      const url = set.tracks.find((t) => t.pos === pos && t.kind !== "embed")?.url;
      if (!url) {
        setSlots((s) => ({ ...s, [name]: { item: it, status: "nobody is streaming this track" } }));
        return;
      }
      const data: DeckData = {
        grid: d.bpm > 0 ? { bpm: d.bpm, offset: d.offset } : null,
        fitErr: d.fitErr,
        peaks: decodePeaks(d.peaks),
        peaksHz: d.peaksHz,
        dur: d.dur,
      };
      deck.load(url, data);
      // A signed URL that dies mid-session: mint another once and carry on
      // from where the deck was.
      let retried = false;
      deck.el.onerror = () => {
        if (retried || !deck.loaded) return;
        retried = true;
        const at = deck.time(), wasPlaying = deck.playing;
        api.streams(release).then((fresh) => {
          const u = fresh.tracks.find((t) => t.pos === pos && t.kind !== "embed")?.url;
          if (!u) return;
          deck.load(u, data);
          deck.seek(at);
          if (wasPlaying) void deck.play().catch(() => undefined);
        }).catch(() => undefined);
      };
      setSlots((s) => ({ ...s, [name]: { item: it, status: null } }));
    } catch (e) {
      const err = e as ApiError;
      const why = err.problem?.type === "deck-pending" ? "still preparing — try again in a moment"
        : err.problem?.type === "deck-failed" ? `could not be prepared: ${err.message}`
        : playError(e);
      setSlots((s) => ({ ...s, [name]: { item: it, status: why } }));
    }
  }

  function sync(name: SlotName) {
    const slave = name === "A" ? A : B, master = name === "A" ? B : A;
    const r = syncTo(slave, master);
    if (!r) { setToast("both decks need a track with a beat grid"); return; }
    if (r.clamped) {
      setToast(`tempos too far apart: ${r.neededPct > 0 ? "+" : ""}${r.neededPct.toFixed(1)}% needed, the fader stops at ±16%`);
    }
  }

  // --- the keyboard -----------------------------------------------------------
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      const deck = e.shiftKey ? B : A;
      switch (e.code) {
        case "Space":
          e.preventDefault();
          void deck.toggle().catch(() => undefined);
          break;
        case "ArrowLeft":
        case "ArrowRight":
          if (!deck.playing) return;
          e.preventDefault();
          deck.bend(e.code === "ArrowRight" ? 1.02 : 0.98, 250);
          break;
        case "KeyS":
          sync("B");
          break;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }); // every render: sync() closes over the current decks, which never change

  // --- the list's edits -------------------------------------------------------
  async function remove(it: TestItem) {
    try {
      await api.removeFromTestList(it.id);
      for (const name of ["A", "B"] as SlotName[]) {
        if (slots[name].item?.id === it.id) {
          (name === "A" ? A : B).unload();
          setSlots((s) => ({ ...s, [name]: { item: null, status: null } }));
        }
      }
      load();
    } catch (e) { setError((e as ApiError).message); }
  }

  async function retry(it: TestItem) {
    if (!it.release || it.pos == null) return;
    try { await api.retryDeck(it.release, it.pos); load(); } catch (e) { setError((e as ApiError).message); }
  }

  const deckWord: Record<TestItem["deck"], string> = {
    none: "not asked", queued: "preparing…", ready: "ready", failed: "failed",
  };

  return (
    <div className="decks-page">
      <h2 className="pl-h">Beat match</h2>
      <p className="dim small">
        Two decks for trying records against each other before buying them. Put
        a track on the list from <b>⋯</b> on its row in the per-track view, load
        it onto A or B, and Sync matches one deck's tempo and beats to the
        other's. The waveform and beat grid are made once per track by the worker.
      </p>
      {error && <div className="error">{error}</div>}

      <div className="decks">
        <DeckPanel slot="A" deck={A} item={slots.A.item} status={slots.A.status} onSync={() => sync("A")} />
        <DeckPanel slot="B" deck={B} item={slots.B.item} status={slots.B.status} onSync={() => sync("B")} />
      </div>
      <p className="keys dim small">
        <kbd>space</kbd> plays A, <kbd>shift</kbd>+<kbd>space</kbd> B · <kbd>←</kbd> <kbd>→</kbd> nudge A
        (with <kbd>shift</kbd>, B) · <kbd>s</kbd> syncs B to A · drag a platter to nudge while playing or to
        scrub while paused · ±16% is the fader's range; key lock keeps the pitch while the tempo moves.
      </p>

      {items && items.length === 0 && (
        <div className="empty">Nothing on the list yet. Press <b>⋯</b> on a track row and choose “Add to Beat match”.</div>
      )}
      {items && items.length > 0 && (
        <table className="tracklist tl-items">
          <tbody>
            {items.map((it) => {
              const can = it.listed && it.streamable && it.deck === "ready";
              const onA = slots.A.item?.id === it.id, onB = slots.B.item?.id === it.id;
              return (
                <tr key={it.id} className={`${onA || onB ? "playing" : ""}${it.listed ? "" : " gone"}`}>
                  <td className="art">{it.art && <img src={it.art} alt="" loading="lazy" width={40} height={40} />}</td>
                  <td className="t">
                    <span className="tt">{it.title}</span>
                    <span className="ta">
                      {it.artist}
                      {it.via && <span className="via"> · clip from {it.via}</span>}
                      {!it.listed && <span className="via"> · no longer listed</span>}
                      {it.listed && !it.streamable && <span className="via"> · nobody streams it</span>}
                    </span>
                    {it.deck === "failed" && it.deckError && <span className="err">{it.deckError}</span>}
                  </td>
                  <td className="al"><span className="dim">{it.album}</span></td>
                  <td className="d">{it.dur ? fmtDur(it.dur) : ""}</td>
                  <td className={`b${it.bpm ? "" : " none"}`}>{it.bpm ?? "—"}</td>
                  <td className={`k${it.key_ ? "" : " none"}`}>{it.key_ ?? "—"}</td>
                  <td className="st">
                    <span className={`state ${it.deck}`}>{deckWord[it.deck]}</span>
                    {it.deck === "failed" && <button className="link" onClick={() => void retry(it)}>retry</button>}
                  </td>
                  <td className="ops">
                    <button className={`ld${onA ? " on" : ""}`} disabled={!can} onClick={() => void loadSlot("A", it)} title="load onto deck A">A</button>
                    <button className={`ld${onB ? " on" : ""}`} disabled={!can} onClick={() => void loadSlot("B", it)} title="load onto deck B">B</button>
                    <button className="link" onClick={() => void remove(it)} title="take it off the list">×</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {!items && !error && <div className="loading"><span className="spinner" />loading…</div>}
      <Toast msg={toast} onGone={clearToast} />
    </div>
  );
}
